import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VehicleModel, fetchVehicleModels } from '../api/vehicleModels';

interface SelectVehicleModelProps {
  brandId?: string;
  value: string;
  onChange: (modelId: string, model: VehicleModel | null) => void;
  id?: string;
  name?: string;
  label?: string;
  labelClassName?: string;
  className?: string;
  selectClassName?: string;
  helperText?: string;
  helperClassName?: string;
  placeholderLabel?: string;
  placeholderValue?: string;
  includePlaceholder?: boolean;
  disabled?: boolean;
  required?: boolean;
  limit?: number;
  renderOptionLabel?: (model: VehicleModel) => string;
  onStatusChange?: (status: { loading: boolean; error: string | null; models: VehicleModel[] }) => void;
  showStatus?: boolean;
}

const joinClasses = (...classes: Array<string | undefined | null | false>) =>
  classes.filter(Boolean).join(' ');

const clampLimit = (limit: number) => {
  if (!Number.isFinite(limit) || limit <= 0) {
    return 100;
  }
  return Math.min(Math.floor(limit), 100);
};

const SelectVehicleModel: FC<SelectVehicleModelProps> = ({
  brandId,
  value,
  onChange,
  id,
  name,
  label,
  labelClassName,
  className,
  selectClassName,
  helperText,
  helperClassName,
  placeholderLabel = 'Select a model',
  placeholderValue = '',
  includePlaceholder = true,
  disabled = false,
  required = false,
  limit = 100,
  renderOptionLabel,
  onStatusChange,
  showStatus = true,
}) => {
  const [models, setModels] = useState<VehicleModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const statusCallbackRef = useRef<typeof onStatusChange>();

  useEffect(() => {
    statusCallbackRef.current = onStatusChange;
  }, [onStatusChange]);

  const emitStatus = useCallback(
    (status: { loading: boolean; error: string | null; models: VehicleModel[] }) => {
      statusCallbackRef.current?.(status);
    },
    [],
  );

  useEffect(() => {
    if (!brandId) {
      setModels([]);
      setError(null);
      setLoading(false);
      emitStatus({ loading: false, error: null, models: [] });
      return;
    }

    let active = true;
    const pageSize = clampLimit(limit);

    setLoading(true);
    setError(null);
    setModels([]);
    emitStatus({ loading: true, error: null, models: [] });

    fetchVehicleModels({ page: 1, limit: pageSize, brandId })
      .then((response) => {
        if (!active) {
          return;
        }
        setModels(response.data);
        setError(null);
        setLoading(false);
        emitStatus({ loading: false, error: null, models: response.data });
      })
      .catch((fetchError) => {
        if (!active) {
          return;
        }
        const message =
          fetchError instanceof Error ? fetchError.message : 'Failed to load vehicle models.';
        setModels([]);
        setError(message);
        setLoading(false);
        emitStatus({ loading: false, error: message, models: [] });
      });

    return () => {
      active = false;
    };
  }, [brandId, limit, emitStatus]);

  const combinedHelperText = useMemo(() => {
    if (loading) {
      return 'Loading vehicle models…';
    }
    if (error) {
      return error;
    }
    return helperText ?? null;
  }, [error, helperText, loading]);

  return (
    <div className={joinClasses('flex flex-col gap-1', className)}>
      {label ? (
        <label htmlFor={id} className={joinClasses('text-xs font-semibold text-slate-600', labelClassName)}>
          {label}
        </label>
      ) : null}

      <select
        id={id}
        name={name}
        value={value}
        onChange={(event) => {
          const nextValue = event.target.value;
          const model = models.find((item) => item.id === nextValue) ?? null;
          onChange(nextValue, model);
        }}
        disabled={disabled || loading || !brandId}
        required={required}
        className={joinClasses(
          'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-slate-100',
          selectClassName,
        )}
      >
        {includePlaceholder ? (
          <option value={placeholderValue}>
            {brandId ? placeholderLabel : 'Select brand first'}
          </option>
        ) : null}
        {models.map((model) => (
          <option key={model.id} value={model.id}>
            {renderOptionLabel ? renderOptionLabel(model) : model.name}
          </option>
        ))}
      </select>
      {showStatus && combinedHelperText ? (
        <p
          className={joinClasses(
            'text-[11px] text-slate-400',
            error ? 'text-danger' : undefined,
            helperClassName,
          )}
        >
          {combinedHelperText}
        </p>
      ) : null}
    </div>
  );
};

export default SelectVehicleModel;
